import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
    LayoutDashboard,
    FileText,
    BookOpen,
    Users,
    Dumbbell,
    Video,
    BarChart3,
    Shield,
    Flag,
    UserPlus,
    Loader2,
    ChevronRight
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useTeam } from '../context/TeamContext';
import { useTranslation } from 'react-i18next';
import { teamsAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';
import PremiumPromotionModal from '../components/PremiumPromotionModal';

const MobileTeamMenu = () => {
    const { user } = useAuth();
    const { plan } = useTeam();
    const isPremium = plan === 'premium';
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { toast } = useToast();

    const [showPremiumModal, setShowPremiumModal] = useState(false);
    const [teamCode, setTeamCode] = useState('');
    const [joining, setJoining] = useState(false);

    const handleJoinTeam = async (e) => {
        e.preventDefault();
        if (!teamCode.trim()) return;

        setJoining(true);
        try {
            await teamsAPI.createJoinRequest(teamCode);
            toast({
                title: t('common.success'),
                description: t('nav.joinRequestSuccess'),
            });
            setTeamCode('');
        } catch (error) {
            console.log(error);
            let errorMessage = t('nav.joinRequestError');
            try {
                const detail = error.response?.data?.detail;
                if (typeof detail === 'string') {
                    errorMessage = detail;
                } else if (Array.isArray(detail)) {
                    errorMessage = detail.map(err => String(err?.msg || 'Validation error')).join(', ');
                } else if (detail && typeof detail === 'object') {
                    errorMessage = detail.message || JSON.stringify(detail);
                }
            } catch (e) {
                console.error("Error parsing error message", e);
            }
            toast({
                variant: "destructive",
                title: t('common.error'),
                description: String(errorMessage),
            });
        } finally {
            setJoining(false);
        }
    };

    const menuItems = [
        { path: '/dashboard', icon: LayoutDashboard, label: t('nav.dashboard') },
        { path: '/notes', icon: FileText, label: t('nav.announcements') },
        { path: '/playbook', icon: BookOpen, label: t('nav.playbook') },
        { path: '/training', icon: Dumbbell, label: t('nav.training') },
        { path: '/videos', icon: Video, label: t('nav.videos') },
        { path: '/roster', icon: Users, label: t('nav.roster') },
        { path: '/stats', icon: BarChart3, label: t('nav.stats'), premiumOnly: true }
    ];

    const adminItems = [
        { path: '/admin', icon: Shield, label: t('nav.admin') }
    ];

    const coachItems = [
        { path: '/analytics', icon: LayoutDashboard, label: t('nav.teamManagement') }
    ];

    const renderLink = (item) => {
        const Icon = item.icon;
        return (
            <Link
                key={item.path}
                to={item.premiumOnly && !isPremium ? '#' : item.path}
                onClick={(e) => {
                    if (item.premiumOnly && !isPremium) {
                        e.preventDefault();
                        setShowPremiumModal(true);
                    }
                }}
                className={`flex items-center p-4 bg-[#1a1a1b] border border-white/5 rounded-2xl transition-all active:scale-[0.98] active:bg-[#2a2a2b] relative overflow-hidden group ${item.premiumOnly && !isPremium ? 'opacity-70' : ''
                    }`}
            >
                <div className="p-3 bg-white/5 rounded-xl mr-4 text-snapp-green group-active:scale-90 transition-transform">
                    <Icon className="w-6 h-6" />
                </div>
                <div className="flex-1">
                    <div className="font-bold text-white text-base">
                        {item.label}
                    </div>
                    {item.premiumOnly && !isPremium && (
                        <div className="text-[10px] text-amber-500 font-bold uppercase tracking-wider mt-0.5 flex items-center gap-1">
                            <Shield className="w-3 h-3" /> {t('common.premium')}
                        </div>
                    )}
                </div>
                <ChevronRight className="w-5 h-5 text-gray-600 group-hover:text-gray-400 group-hover:translate-x-1 transition-all" />
            </Link>
        );
    };

    return (
        <div className="min-h-screen bg-[#0a0a0b] pb-24 animate-in fade-in duration-500">
            {/* Premium Sticky Header */}
            <div className="p-4 bg-[#0a0a0b]/80 backdrop-blur-xl sticky top-0 z-10 border-b border-white/5">
                <div className="flex items-center justify-between">
                    <h1 className="text-2xl font-black text-white tracking-tight uppercase">
                        {t('nav.team', 'Takım')}
                    </h1>
                    {user?.team_id && (
                        <div className="px-3 py-1 bg-snapp-purple/10 border border-snapp-purple/20 rounded-full">
                            <span className="text-[10px] font-black text-snapp-purple uppercase tracking-widest">{t(`common.${user.role}`)}</span>
                        </div>
                    )}
                </div>
            </div>

            <div className="p-4 space-y-8">
                {/* Team Header Status */}
                {user?.team_id ? (
                    <Link
                        to={`/teams/${user.team_id}/profile`}
                        className="flex items-center p-5 bg-gradient-to-br from-snapp-purple/20 via-snapp-blue/10 to-[#1a1a1b] border border-white/10 rounded-[2rem] relative overflow-hidden shadow-2xl shadow-snapp-purple/10 group active:scale-[0.98] transition-transform"
                    >
                        <div className="absolute top-0 right-0 w-32 h-32 bg-snapp-purple/10 blur-[50px] -mr-16 -mt-16" />

                        {user.team_logo_url ? (
                            <div className="relative">
                                <img src={user.team_logo_url} alt={user.team_name} className="w-16 h-16 rounded-full object-contain bg-black/40 border border-white/10 p-1 mr-4 shadow-xl" />
                                <div className="absolute -bottom-1 -right-1 bg-snapp-purple w-5 h-5 rounded-full flex items-center justify-center border-2 border-black">
                                    <Shield className="w-3 h-3 text-white" />
                                </div>
                            </div>
                        ) : (
                            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-snapp-purple/30 to-snapp-blue/30 flex items-center justify-center mr-4 border border-white/10 shadow-xl">
                                <Flag className="w-8 h-8 text-white/80" />
                            </div>
                        )}
                        <div className="flex-1">
                            <h2 className="text-xl font-black text-white leading-tight">{user.team_name}</h2>
                            <div className="flex items-center gap-2 mt-1">
                                <span className="text-sm text-gray-400 font-medium">{t('profile.viewProfile', 'Profile Git')}</span>
                                <ChevronRight className="w-4 h-4 text-gray-600 group-hover:translate-x-1 transition-transform" />
                            </div>
                        </div>
                    </Link>
                ) : (
                    <div className="p-6 bg-[#1a1a1b] border border-white/5 rounded-[2rem] shadow-xl relative overflow-hidden">
                        <div className="absolute top-0 right-0 p-4 opacity-10">
                            <UserPlus className="w-16 h-16 text-white" />
                        </div>
                        <h2 className="text-2xl font-black text-white mb-2">{t('nav.joinTeamTitle', 'Takıma Katıl')}</h2>
                        <p className="text-sm text-gray-400 mb-6 leading-relaxed">{t('nav.joinTeamDesc', 'Bir takım kodunuz varsa girin ve ekibinize hemen katılın.')}</p>
                        <form onSubmit={handleJoinTeam} className="space-y-3">
                            <div className="relative group">
                                <input
                                    type="text"
                                    placeholder={t('nav.teamCodePlaceholder')}
                                    value={teamCode}
                                    onChange={(e) => setTeamCode(e.target.value.toUpperCase())}
                                    className="w-full bg-black/40 border border-white/10 focus:border-snapp-purple/50 rounded-2xl px-4 py-4 text-white uppercase tracking-[0.3em] font-black text-center text-xl transition-all outline-none"
                                />
                                <div className="absolute inset-0 rounded-2xl bg-snapp-purple/5 opacity-0 group-focus-within:opacity-100 pointer-events-none transition-opacity" />
                            </div>
                            <button
                                type="submit"
                                disabled={joining || !teamCode.trim()}
                                className="w-full bg-gradient-to-r from-snapp-purple to-snapp-blue hover:from-snapp-purple/90 hover:to-snapp-blue/90 disabled:opacity-50 text-white font-black px-4 py-4 rounded-2xl transition-all flex items-center justify-center shadow-lg shadow-snapp-purple/20 active:scale-[0.98]"
                            >
                                {joining ? <Loader2 className="w-6 h-6 animate-spin" /> : t('nav.joinTeam')}
                            </button>
                        </form>
                    </div>
                )}

                {/* Team Menu Items (If in a team) */}
                {user?.team_id && (
                    <div className="space-y-8 pt-2">
                        <div className="space-y-4">
                            <div className="flex items-center gap-3">
                                <div className="h-px flex-1 bg-gradient-to-r from-transparent to-white/10" />
                                <h3 className="text-[10px] font-black text-gray-500 uppercase tracking-[0.3em]">{t('nav.teamFeatures', 'Takım Özellikleri')}</h3>
                                <div className="h-px flex-1 bg-gradient-to-l from-transparent to-white/10" />
                            </div>
                            <div className="grid grid-cols-1 gap-3">
                                {menuItems.map(renderLink)}
                            </div>
                        </div>

                        {(user.role === 'coach' || user.role === 'admin') && (
                            <div className="space-y-4">
                                <div className="flex items-center gap-3">
                                    <div className="h-px flex-1 bg-gradient-to-r from-transparent to-white/10" />
                                    <h3 className="text-[10px] font-black text-snapp-purple uppercase tracking-[0.3em]">{t('nav.teamManagement')}</h3>
                                    <div className="h-px flex-1 bg-gradient-to-l from-transparent to-white/10" />
                                </div>
                                <div className="grid grid-cols-1 gap-3">
                                    {coachItems.map(renderLink)}
                                </div>
                            </div>
                        )}

                        {user.role === 'admin' && (
                            <div className="space-y-4">
                                <div className="flex items-center gap-3">
                                    <div className="h-px flex-1 bg-gradient-to-r from-transparent to-red-500/10" />
                                    <h3 className="text-[10px] font-black text-red-500 uppercase tracking-[0.3em]">System Admin</h3>
                                    <div className="h-px flex-1 bg-gradient-to-l from-transparent to-red-500/10" />
                                </div>
                                <div className="grid grid-cols-1 gap-3">
                                    {adminItems.map(renderLink)}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Favorite Team Section (Enhanced) */}
                {(!user?.team_id) && user?.membership_type !== 'seller' && (
                    <div className="space-y-4 pt-4">
                        <div className="flex items-center gap-3">
                            <div className="h-px flex-1 bg-gradient-to-r from-transparent to-white/10" />
                            <h3 className="text-[10px] font-black text-gray-500 uppercase tracking-[0.3em]">{t('profile.favoriteTeam', 'Favori Takımım')}</h3>
                            <div className="h-px flex-1 bg-gradient-to-l from-transparent to-white/10" />
                        </div>

                        {user?.favorite_team_id ? (
                            <Link
                                to={`/teams/${user.favorite_team_id}/profile`}
                                className="flex items-center p-5 bg-[#1a1a1b] border border-white/5 rounded-[2rem] relative overflow-hidden active:scale-[0.98] transition-transform group"
                            >
                                <div className="p-3 bg-amber-500/10 rounded-xl mr-4 text-amber-500">
                                    <Shield className="w-6 h-6" />
                                </div>
                                <div className="flex-1">
                                    <h4 className="font-bold text-white text-base">
                                        {user?.favorite_team_name || t('profile.favoriteTeam', 'Favori Takımım')}
                                    </h4>
                                    <p className="text-[10px] text-gray-500 font-bold uppercase tracking-wider mt-0.5">Quick Access</p>
                                </div>
                                <ChevronRight className="w-5 h-5 text-gray-600 group-hover:text-gray-400 group-hover:translate-x-1 transition-all" />
                            </Link>
                        ) : (
                            <div className="p-8 bg-[#1a1a1b] border border-white/5 border-dashed rounded-[2rem] text-center flex flex-col items-center justify-center">
                                <div className="w-12 h-12 bg-white/5 rounded-full flex items-center justify-center mb-3">
                                    <Shield className="w-6 h-6 text-gray-600" />
                                </div>
                                <p className="text-gray-400 text-sm font-medium">{t('profile.noFavoriteTeam', 'Henüz favori takım seçilmedi.')}</p>
                            </div>
                        )}
                    </div>
                )}
            </div>

            <PremiumPromotionModal
                open={showPremiumModal}
                onOpenChange={setShowPremiumModal}
            />
        </div>
    );
};

export default MobileTeamMenu;
